import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { findCityBySlug, cities, photographerMatchesCity, type CityInfo } from "@/data/cities";
import { visagistThemes, type Visagist } from "@/data/visagists";
import { fetchAllVisagists } from "@/lib/visagists-source";
import { PortraitImage } from "@/components/PortraitImage";

function intro(city: CityInfo): string {
  return `Kuratierte Visagist:innen in ${city.name}, Tages-Make-Up, Abend-Make-Up, Brautstyling und Editorial. OM Selected empfiehlt geprüfte Visagist:innen aus ${city.name} und Umgebung. Kein Marktplatz, kein Algorithmus, eine redaktionelle Auswahl.`;
}

export const Route = createFileRoute("/visagist/$city")({
  loader: ({ params }) => {
    const city = findCityBySlug(params.city);
    if (!city) throw notFound();
    return { city };
  },
  head: ({ loaderData, params }) => {
    const city = loaderData?.city;
    if (!city) return { meta: [{ title: "Visagist:in, OM Selected" }] };
    const title = `Visagist:in ${city.name} finden, OM Selected`;
    const description = `Kuratierte Visagist:innen in ${city.name} (${city.country}) für Brautstyling, Editorial, Tages- und Abend-Make-Up. Redaktionell empfohlen von OM Selected.`;
    const url = `https://omselected.art/visagist/${params.city}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: title,
            description,
            url,
            about: {
              "@type": "Place",
              name: city.name,
              address: { "@type": "PostalAddress", addressLocality: city.name, addressCountry: city.country },
            },
            isPartOf: { "@type": "WebSite", name: "OM Selected", url: "https://omselected.art" },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
      <h1 className="serif text-4xl">Stadt nicht gefunden</h1>
      <Link to="/visagists" className="eyebrow inline-block mt-8 border-b border-foreground pb-1">
        Alle Visagist:innen ansehen
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
      <h1 className="serif text-4xl">Fehler</h1>
      <p className="mt-4 text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: CityPage,
});

function CityPage() {
  const { t } = useTranslation();
  const { city } = Route.useLoaderData() as { city: CityInfo };
  const [all, setAll] = useState<Visagist[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetchAllVisagists().then((data) => {
      if (active) {
        setAll(data);
        setLoading(false);
      }
    });
    return () => { active = false; };
  }, []);

  const matching = all.filter(
    (p) => photographerMatchesCity(p.city, city.name) || photographerMatchesCity(p.region, city.name),
  );
  const nearby = cities.filter((c) => c.country === city.country && c.slug !== city.slug).slice(0, 8);

  return (
    <div>
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-12 pb-16">
          <div className="eyebrow text-muted-foreground">Visagist:in finden · {city.country}</div>
          <h1 className="serif text-5xl md:text-7xl mt-6 leading-[0.95]">
            Visagist:in in {city.name}
          </h1>
          <p className="serif italic text-xl md:text-2xl mt-8 max-w-2xl">{intro(city)}</p>
          <div className="mt-8 flex flex-wrap gap-4 eyebrow text-muted-foreground">
            {visagistThemes.map((t) => (
              <Link
                key={t.id}
                to="/visagist/$city/$theme"
                params={{ city: city.slug, theme: t.id }}
                className="hover:text-foreground"
              >
                {t.label} in {city.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16">
          <h2 className="eyebrow text-muted-foreground mb-8 font-normal">
            Visagist:innen in {city.name}
          </h2>
          {loading ? (
            <div className="text-xs text-muted-foreground italic">{t("common.loading")}</div>
          ) : matching.length === 0 ? (
            <div className="space-y-6 max-w-xl">
              <p className="text-muted-foreground">
                Noch keine gelisteten Visagist:innen in {city.name}. Sieh dir unsere gesamte
                Auswahl an oder sende eine Anfrage, wir schlagen passende Profile vor.
              </p>
              <div className="flex flex-wrap gap-4 eyebrow">
                <Link to="/visagists" className="border-b border-foreground pb-1">Alle Visagist:innen</Link>
                <Link to="/brief" className="border-b border-foreground pb-1">Anfrage senden</Link>
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {matching.map((p) => (
                <Link key={p.id} to="/visagists/$id" params={{ id: p.id }} className="block group cursor-pointer">
                  <PortraitImage p={p} />
                  <div className="mt-4 flex justify-between items-baseline">
                    <h3 className="serif text-2xl">{p.name}</h3>
                    <div className="eyebrow text-muted-foreground">{p.city ?? p.region}</div>
                  </div>
                  <div className="mt-2 eyebrow text-muted-foreground">
                    {p.themes.map((t) => visagistThemes.find((th) => th.id === t)?.label).join(" · ")}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16">
          <h2 className="eyebrow text-muted-foreground mb-6 font-normal">
            Weitere Städte in {city.country}
          </h2>
          <div className="flex flex-wrap gap-x-6 gap-y-3 serif text-xl">
            {nearby.map((c) => (
              <Link key={c.slug} to="/visagist/$city" params={{ city: c.slug }} className="hover:underline">
                Visagist:in {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}